import { useLayoutEffect, useState } from 'react';

import { recordBootEvent } from '../../../bootstrap/boot-trace';
import type { Workspace } from '../entities/workspace';
import type { WorkspaceMetadata } from '../metadata';
import type { WorkspacesService } from '../services/workspaces';

interface ConfigRevalidationServer {
  waitForConfigRevalidation(signal?: AbortSignal): Promise<void>;
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * Opens a workspace from locally cached metadata before remote server config
 * revalidation completes. Socket URL selection already prefers workspace
 * metadata and stored config, so remote config must not block warm-cache boot.
 */
export function useCachedFirstWorkspaceOpen({
  meta,
  server,
  workspacesService,
}: {
  meta: WorkspaceMetadata;
  server?: ConfigRevalidationServer | null;
  workspacesService: WorkspacesService;
}): Workspace | null {
  const [workspace, setWorkspace] = useState<Workspace | null>(null);

  useLayoutEffect(() => {
    const abortController = new AbortController();
    recordBootEvent('workspace-open-started', { flavour: meta.flavour });
    const ref = workspacesService.open({ metadata: meta });

    setWorkspace(ref.workspace);
    recordBootEvent('workspace-opened', { flavour: meta.flavour });

    if (server) {
      void server
        .waitForConfigRevalidation(abortController.signal)
        .catch(error => {
          if (!isAbortError(error)) {
            console.error(error);
          }
        });
    }

    return () => {
      abortController.abort();
      ref.dispose();
    };
  }, [meta, server, workspacesService]);

  return workspace;
}
